/**
 * Drops repeat reports of the same error within a short window, because one
 * crash reaches the reporter from several layers at once. Whichever arrives
 * first claims the fingerprint; see `./README.md` for why the ordering matters.
 */

const DEFAULT_TTL_MS = 5_000;
const MAX_ENTRIES = 100;

/**
 * Runs on raw `unknown`, before normalisation, so every read is guarded — a
 * value that cannot be fingerprinted gets a unique key rather than a shared
 * one, since reporting twice beats collapsing two unrelated errors into one.
 */
export const fingerprint = (value: unknown): string => {
  try {
    if (value instanceof Error) {
      const name = typeof value.name === 'string' ? value.name : 'Error';
      const message = typeof value.message === 'string' ? value.message : '';
      // Line 1, not 0: the header repeats the message. The frame separates
      // same-message errors thrown from different places.
      const firstFrame =
        typeof value.stack === 'string' ? (value.stack.split('\n')[1] ?? '').trim() : '';
      return `${name}|${message}|${firstFrame}`;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return `NonError|${String(value)}`;
    }
    if (value == null) {
      return `NonError|${String(value)}`;
    }
    return `NonError|${JSON.stringify(value)}`;
  } catch {
    // A throwing getter, a cycle, or a hostile toString. Unique, so it reports.
    return `Unfingerprintable|${Math.random()}`;
  }
};

export interface Dedupe {
  /** `true` when the caller should report; `false` when it is a repeat. */
  claim(value: unknown): boolean;
}

// Only expired entries are dropped, so the map is bounded by the number of
// distinct errors inside one TTL rather than by MAX_ENTRIES exactly.

export const createDedupe = ({ ttlMs = DEFAULT_TTL_MS } = {}): Dedupe => {
  const seen = new Map<string, number>();

  return {
    claim: (value) => {
      const key = fingerprint(value);
      const now = Date.now();
      const previous = seen.get(key);

      if (previous !== undefined && now - previous < ttlMs) {
        return false;
      }

      seen.set(key, now);

      if (seen.size > MAX_ENTRIES) {
        for (const [entry, at] of seen) {
          if (now - at >= ttlMs) {
            seen.delete(entry);
          }
        }
      }

      return true;
    },
  };
};
